import { Component, OnInit, Renderer2, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import * as Cookies from 'js-cookie';
import { environment } from '../../../environments/environment';

export interface VideoGuide {
  id: string;
  label: string;
  wistiaId: string;
}

@Component({
  selector: 'app-onboarding-tutorial-modal',
  templateUrl: './onboarding-tutorial-modal.component.pug',
  styleUrls: ['./onboarding-tutorial-modal.component.scss'],
  standalone: false,
})
export class OnboardingTutorialModalComponent implements OnInit, AfterViewInit {
  private static readonly COOKIE_KEY = 'xref-onboarding-video-guides';

  @ViewChild('playerContainer', { static: false }) playerContainer!: ElementRef<HTMLElement>;

  videoGuides: VideoGuide[] = [
    { id: 'navigating', label: 'Navigating Xref', wistiaId: 'khfqevpf59' },
    { id: 'survey', label: 'Creating a survey', wistiaId: 'd3aen41hrj' },
    { id: 'reference', label: 'Sending a Reference', wistiaId: 'f6iom94037' },
    { id: 'pulse', label: 'Sending a Pulse', wistiaId: '6wk9i7usxw' },
    { id: 'exit', label: 'Sending an Exit', wistiaId: '4hqfz8lwkn' },
  ];
  activeGuideIndex = 0;

  constructor(
    public bsModalRef: BsModalRef,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    OnboardingTutorialModalComponent.markAsDismissed();
    this.loadWistiaPlayerScript();
    this.preloadAllEmbedScripts();
  }

  ngAfterViewInit(): void {
    this.insertPlayer(this.videoGuides[this.activeGuideIndex].wistiaId);
    this.setBackdropOpacity();
  }

  selectGuide(index: number): void {
    if (index === this.activeGuideIndex) return;
    this.activeGuideIndex = index;
    this.insertPlayer(this.videoGuides[index].wistiaId);
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  static wasDismissed(): boolean {
    return Cookies.get(OnboardingTutorialModalComponent.COOKIE_KEY) !== 'true';
  }

  static markAsDismissed(): void {
    Cookies.remove(OnboardingTutorialModalComponent.COOKIE_KEY, { domain: environment.COOKIE_DOMAIN });
  }

  private insertPlayer(mediaId: string): void {
    const container = this.playerContainer?.nativeElement;
    if (!container) return;

    container.innerHTML = '';

    const player = document.createElement('wistia-player');
    player.setAttribute('media-id', mediaId);
    player.setAttribute('aspect', '1.7777777777777777');
    container.appendChild(player);
  }

  private loadWistiaPlayerScript(): void {
    if (document.querySelector('script[src*="fast.wistia.com/player.js"]')) return;

    const script = this.renderer.createElement('script');
    script.src = 'https://fast.wistia.com/player.js';
    script.async = true;
    this.renderer.appendChild(document.head, script);
  }

  private preloadAllEmbedScripts(): void {
    const uniqueIds = new Set(this.videoGuides.map((g) => g.wistiaId));
    uniqueIds.forEach((mediaId) => this.loadWistiaEmbedScript(mediaId));
  }

  private loadWistiaEmbedScript(mediaId: string): void {
    const selector = `script[src*="fast.wistia.com/embed/${mediaId}.js"]`;
    if (document.querySelector(selector)) return;

    const script = this.renderer.createElement('script');
    script.src = `https://fast.wistia.com/embed/${mediaId}.js`;
    script.async = true;
    script.type = 'module';
    this.renderer.appendChild(document.head, script);
  }

  private setBackdropOpacity(): void {
    const backdrop = document.querySelector('.modal-backdrop.show') as HTMLElement | null;
    if (backdrop) {
      backdrop.style.opacity = '0.75';
    }
  }
}
